import React, { useState, useEffect } from 'react';
import { map, find } from 'lodash-es';

import Grid, { GridSize } from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { FieldErrors as ReactHookFieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';

import { injectStores } from '@stores/factories/store-utils';
import { useTrans } from '@/custom-hooks/useTrans';
import { DropDownItems } from '../../controls/dropdowns/dropdown';
import { TypeApplicationStore } from '@stores/application-store';
import { useWindowSize } from '@/custom-hooks';

import FieldErrors from '../../molecules/field-errors/field-errors';
import FieldOverlay from '../../atoms/field-overlay/field-overlay';
import TextFieldDropdown from '../text-field-dropdown/text-field-dropdown';

interface ListItemWithKey {
    key: string | undefined;
    value: string | undefined;
}

type ValueType = string | boolean | DropDownItems | any;

type CountryRegionPostalCodeProps = {
    ApplicationStore: TypeApplicationStore;
    dictionaryBase: string;
    register: UseFormRegister<FieldValues>;
    errors: ReactHookFieldErrors<FieldValues>;
    validation: any;
    countryValidation?: any;
    regionValidation?: any;
    zipValidation?: any;
    gridColumnSpan: GridSize;
    overlay?: JSX.Element;
    onChange: (idSuffix: string, newValue?: ValueType) => void;
};

function CountryRegionPostalCode(props: CountryRegionPostalCodeProps) {
    const {
        ApplicationStore: {
            countriesRegionsStore: {
                countries,
                setStoreCountryCode,
                rawDetailsPerCountry,
                regions,
                zipMask,
            },
        },
        dictionaryBase,
        register,
        errors,
        overlay,
        onChange,
    } = props;
    const { t } = useTrans();
    const { isMobile } = useWindowSize();

    const [data, setData] = useState({ countryCode: '', regionCode: '', zip: '' });
    const setDataById = (id: string, val: string) => {
        setData((data) => ({
            ...data,
            [id]: val,
        }));
    };

    const countriesList = countries();
    const regionsList = regions();

    useEffect(
        () => setStoreCountryCode(data.countryCode),
        [rawDetailsPerCountry, data.countryCode]
    );

    const formLabelBase = `${dictionaryBase}.formLabels`;
    const countryFieldLabel = t(`${formLabelBase}.countryCode.label`) as string;
    const regionFieldLabel = t(`${formLabelBase}.regionCode.label`) as string;
    const postalFieldLabel = t(`${formLabelBase}.zip.label`) as string;

    const buildOptionList = (optionList: ListItemWithKey[], thingToSelect: string) => {
        return [
            <MenuItem key='item0' value=''>
                {t('labels.select', { AK_TAG_THING_TO_SELECT: thingToSelect })}
            </MenuItem>,
            ...map(optionList, (item: ListItemWithKey) => (
                <MenuItem key={item.key} value={item.key}>
                    {item.value}
                </MenuItem>
            )),
        ];
    };

    const isValidCountry = find(
        countriesList,
        (opt: ListItemWithKey) => opt.key === data.countryCode
    );
    const isValidRegion = find(regionsList, (opt: ListItemWithKey) => opt.key === data.regionCode);

    const itemWidth = isMobile ? 12 : 6;
    const errorDictionaryBase =
        'I18N:learning:create-school-account.validation.school-address-countryRegionPostalCodes';
    return (
        <>
            <FieldOverlay>{overlay}</FieldOverlay>

            <Grid item xs={regionsList.length ? itemWidth : 12}>
                <TextFieldDropdown
                    id='countryCode'
                    errors={errors}
                    overlay={overlay}
                    value={isValidCountry ? data.countryCode : ''}
                    label={countryFieldLabel as string}
                    textFieldOpts={register('countryCode', {
                        validate: (v, f) =>
                            isValidCountry || `${errorDictionaryBase}.countryRequired`,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                            setDataById('countryCode', event.target.value);
                            onChange?.('countryCode', event.target.value);
                        },
                    })}
                    helperText={errors.currency?.message as string}
                >
                    {buildOptionList(countriesList, countryFieldLabel)}
                </TextFieldDropdown>
            </Grid>
            {data.countryCode && regionsList.length ? (
                <Grid item xs={itemWidth}>
                    <TextFieldDropdown
                        id='regionCode'
                        errors={errors}
                        overlay={overlay}
                        value={isValidRegion ? data.regionCode : ''}
                        label={regionFieldLabel as string}
                        textFieldOpts={register('regionCode', {
                            validate: (v, f) =>
                                isValidRegion || `${errorDictionaryBase}.regionRequired`,
                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                setDataById('regionCode', event.target.value);
                                onChange?.('regionCode', event.target.value);
                            },
                        })}
                        helperText={errors.currency?.message as string}
                    >
                        {buildOptionList(regionsList, regionFieldLabel)}
                    </TextFieldDropdown>
                </Grid>
            ) : null}
            {data.countryCode ? (
                <Grid item xs={12}>
                    <TextField
                        inputProps={{}}
                        id='zip'
                        variant='outlined'
                        label={postalFieldLabel}
                        type='text'
                        fullWidth
                        value={data.zip}
                        {...register('zip', {
                            validate: (v, f) => {
                                const { regionPostalCodeRegex, regionPostalCodeReadableRegex } = {
                                    ...zipMask(),
                                };
                                const regex = new RegExp(regionPostalCodeRegex || '');
                                return regex.test(v) || regionPostalCodeReadableRegex;
                            },
                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                setDataById('zip', event.target.value);
                                onChange?.('zip', event.target.value);
                            },
                        })}
                    />
                    <FieldErrors fieldId='zip' errors={errors} />
                </Grid>
            ) : null}
        </>
    );
}

export default injectStores(['ApplicationStore'])(CountryRegionPostalCode);
